import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Check, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/Card";
import { IconButton } from "@/components/ui/IconButton";
import { Tabs } from "@/components/ui/Tabs";
import { useStored } from "@/hooks/useStored";
import { cn } from "@/lib/cn";

/** One row: a queue with a number on it, or a single thing with a mark. */
export interface InboxItem {
  /** The same for as long as the row is about the same thing. */
  key: string;
  /** How many are waiting. Left out for a row that is one thing, not a queue. */
  count?: number;
  /** Drawn where the number would be, when there is no number. */
  mark?: ReactNode;
  /** The plain sentence. */
  label: string;
  /** A quieter second line: how much is asked, and by when. */
  detail?: string;
  /** Where the row leads. */
  link: string;
  urgency: "urgent" | "normal" | "low";
  /** The word at the end of the row. "Open" unless the row says otherwise. */
  verb?: string;
}

type InboxTab = "all" | "important" | "other" | "cleared";

export interface InboxProps {
  items: InboxItem[];
  /**
   * Where this person's cleared rows are kept in their browser.
   *
   * One per screen, and one per person where a computer is shared: a row
   * cleared on the HR home says nothing about the employee home, and a row
   * one teller cleared at the counter is still news to the next.
   */
  storageKey: string;
  /** The line under the heading, given how many rows have not been cleared. */
  describe: (live: number) => string;
  /** The longer explanation behind the question mark. */
  help: ReactNode;
  /** What stands in for the whole panel when nothing is waiting at all. */
  empty: ReactNode;
  /** The panel's grip and its trailing controls, where the screen has them. */
  grip?: boolean;
  actions?: ReactNode;
}

/**
 * Everything waiting on one person, as an inbox.
 *
 * An inbox is a list somebody works down: what matters first, the
 * rest behind a tab, and anything dealt with cleared out of the way. The HR
 * home and the employee home both draw this one, so the tabs, clearing and
 * the way back behave the same on both.
 *
 * Clearing is this person's own note to themselves, kept in their browser,
 * and it changes nothing for anybody else: the queue is still there for a
 * colleague and still counted in the sidebar. A cleared row comes back by
 * itself when its number changes, because "seventeen signatures outstanding"
 * put aside this morning is not the same news as eighteen this afternoon.
 */
export function Inbox({ items, storageKey, describe, help, empty, grip, actions }: InboxProps) {
  const [tab, setTab] = useState<InboxTab>("all");
  // Row key to the count it had when it was cleared.
  const [cleared, setCleared] = useStored<Record<string, number>>(storageKey, {});

  const isCleared = (item: InboxItem) => cleared[item.key] === stamp(item);
  const live = items.filter((item) => !isCleared(item));
  const important = live.filter((item) => item.urgency === "urgent");
  const other = live.filter((item) => item.urgency !== "urgent");
  const put_away = items.filter(isCleared);
  const shown =
    tab === "important" ? important : tab === "other" ? other : tab === "cleared" ? put_away : live;

  function clear(item: InboxItem) {
    setCleared((current) => ({ ...current, [item.key]: stamp(item) }));
    toast(`Cleared: ${item.label}`, {
      description:
        item.count === undefined
          ? "It is under Cleared until it is dealt with."
          : "It comes back when the number changes.",
      action: { label: "Undo", onClick: () => restore(item) },
    });
  }

  function restore(item: InboxItem) {
    setCleared((current) => {
      const next = { ...current };
      delete next[item.key];
      return next;
    });
  }

  if (items.length === 0) return <>{empty}</>;

  return (
    <Card
      title="Waiting on you"
      description={describe(live.length)}
      help={help}
      grip={grip}
      actions={actions}
      flush
    >
      <Tabs<InboxTab>
        className="mt-3 px-5"
        label="Waiting on you"
        value={tab}
        onChange={setTab}
        items={[
          { value: "all", label: "All", count: live.length },
          { value: "important", label: "Important", count: important.length, countWaiting: true },
          { value: "other", label: "Other", count: other.length },
          { value: "cleared", label: "Cleared", count: put_away.length },
        ]}
      />
      {shown.length === 0 ? (
        <p className="px-5 py-8 text-center text-base text-fg-subtle">
          {tab === "cleared"
            ? "Nothing has been cleared."
            : tab === "important"
              ? "Nothing needs doing today."
              : "Nothing here."}
        </p>
      ) : (
        <ul className="divide-y divide-line-faint">
          {shown.map((item) => (
            <li
              key={item.key}
              className="group flex items-center gap-1 pr-3 transition-colors hover:bg-surface-sunken"
            >
              <Link to={item.link} className="flex min-w-0 flex-1 items-center gap-4 py-3 pl-5 pr-2">
                <span
                  className={cn(
                    "grid size-9 shrink-0 place-items-center rounded-sm text-base font-bold tabular-nums",
                    // Tinted, not filled. Six solid blocks of colour running down
                    // the middle of a page is a stack of buttons, not a list.
                    item.urgency === "urgent"
                      ? "bg-alert-50 text-alert-700 ring-1 ring-inset ring-alert-100"
                      : item.urgency === "normal"
                        ? "bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100"
                        : "bg-ink-100 text-fg-muted ring-1 ring-inset ring-ink-200",
                    tab === "cleared" && "opacity-60",
                  )}
                  aria-hidden={item.count === undefined ? true : undefined}
                >
                  {item.count ?? item.mark}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block truncate text-base font-medium",
                      tab === "cleared" ? "text-fg-subtle" : "text-fg",
                    )}
                  >
                    {item.label}
                  </span>
                  {item.detail ? (
                    <span className="block truncate text-xs text-fg-subtle">{item.detail}</span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs font-medium text-fg-subtle transition-colors group-hover:text-fg">
                  {item.verb ?? "Open"}
                </span>
              </Link>
              {tab === "cleared" ? (
                <IconButton
                  size="sm"
                  label="Put back"
                  icon={<Undo2 className="size-4" />}
                  onClick={() => restore(item)}
                />
              ) : (
                <IconButton
                  size="sm"
                  label="Clear"
                  icon={<Check className="size-4" />}
                  onClick={() => clear(item)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * What a row has to still be saying for it to stay cleared.
 *
 * For a queue that is its number. A row that is one thing has no number to
 * change, so it stays put away until the thing itself is gone.
 */
function stamp(item: InboxItem): number {
  return item.count ?? 1;
}
